import { MeasurementUnitEnum, MeasureType } from 'domains/measurementDomain';
import {
  basisWeightAbbrev,
  unitAbbrev,
  unitSymbol, isMetric } from 'utils/measurement';
import { getFormatDate } from 'components/RevxDate';
import { toast } from 'components/RevxMessage';
import { User } from 'domains/appDomain';
import { CartLineItem } from 'domains/cartDomain';
import {
  GallerySku,
  SkuGalleryLabel,
  SkuGalleryLabelCount,
} from 'domains/gallerySkuDomain';
import {
  ExclusiveTypeEnum,
  JobConfig,
  JobConfigSvcOptionType,
  JobOptionDef,
  JobSpec,
  PaperChooseTypeEnum,
} from 'domains/jobconfigDomain';
import { PurchaseOrder } from 'domains/orderDomain';
import {
  CustomRuleCodeEnum,
  InkEnum,
  OrientationEnum,
  SKU,
  SkuSizePayload,
} from 'domains/skuDomain';
import { Svc, SvcType } from 'domains/svcDomain';
import i18n from 'i18next';
import _ from 'lodash';
import { sum } from 'lodash-es';
import moment from 'moment';
import { StepTypeEnum } from './stepTypeEnum';
import React from 'react';
import { SKU_TYPE_NAME_PACKAGING } from './constants';
import { cubeOrganizeStructure } from './filter';
import { getFriendlyDisplay } from './misc';
import { isSourcer, isSupplier } from './rbac';

export const getSelectedSpecItem = (configItem: JobConfig) => {
  return (
    configItem?.specItems?.find((specItem) => specItem?.selected) ||
    configItem?.specItems?.[0]
  );
};
export const getJobConfigQtyString = (configItem: JobConfig) => {
  return configItem.specItems
    ?.map((specItem) => formatNumber(specItem.quantity))
    .join(' / ');
};

export const getJobConfigPageCountString = (configItem: JobConfig) => {
  const selectedSpecItem = getSelectedSpecItem(configItem);

  const spec = selectedSpecItem.spec;
  const part = spec.parts.find((item) => item.pageCountEditable);
  return part?.pageCount || '';
};

export const getJobConfigSizeOrientationString = (
  configItem: JobConfig,
  unit: MeasurementUnitEnum
) => {
  const selectedSpecItem = getSelectedSpecItem(configItem);

  // The API sends both units; pick the one being viewed. finishedSizeIn stays imperial because
  // the size picker matches a saved selection on it and posts it back.
  const size = isMetric(unit)
    ? selectedSpecItem.spec.finishedSizeMetric ?? selectedSpecItem.spec.finishedSizeIn
    : selectedSpecItem.spec.finishedSizeIn;
  return (
    // The step summary is a bare label, so the size says what unit it is in. No space before the
    // unit: the step title is rendered under `text-transform: capitalize`, which would turn a
    // free-standing "mm" into "Mm".
    [size && `${size}${unitAbbrev(MeasureType.LENGTH, unit)}`, getFriendlyDisplay(selectedSpecItem.spec.orientation)]
      .filter(Boolean)
      .join(' - ')
  );
};

export const getPackagingJobConfigSizeOrientationString = (
  configItem: JobConfig,
  unit: MeasurementUnitEnum
) => {
  const selectedSpecItem = getSelectedSpecItem(configItem);
  // Take the axes off whichever string is being viewed, so nothing is scaled a second time.
  const cubeSize = cubeOrganizeStructure(
    (isMetric(unit)
      ? selectedSpecItem.spec.finishedSizeMetric ?? selectedSpecItem.spec.finishedSizeIn
      : selectedSpecItem.spec.finishedSizeIn) || ''
  );
  const symbol = unitSymbol(MeasureType.LENGTH, unit);
  return `${cubeSize.width}${symbol}W x ${cubeSize.length}${symbol}L x ${cubeSize.height}${symbol}H`;
};

export const getJobSpecPaperString = (
  partSpec: JobSpec,
  unit: MeasurementUnitEnum
) => {
  // Both units arrive together; the raw paperBasisWeight stays what the supplier typed.
  const weight = isMetric(unit)
    ? partSpec.paperBasisWeightMetric ?? partSpec.paperBasisWeight
    : partSpec.paperBasisWeight;
  // an unconvertible value keeps the supplier's own string, which already carries its unit
  // no space before the unit - see getJobConfigSizeOrientationString
  const weightShown =
    weight && weight !== String(partSpec.paperBasisWeight ?? '')
      ? `${weight}${basisWeightAbbrev(unit)}`
      : weight;
  let paperTitle =
    weightShown + ' ' + getFriendlyDisplay(partSpec.paperCategory);
  if (partSpec.paperChooseType === PaperChooseTypeEnum.SEARCH) {
    paperTitle = getFriendlyDisplay(partSpec.paperBrand);
  }

  return paperTitle;
};

export const isSkuBooklets = (skuName?: string, skuTypeName?: string) => {
  return (
    skuTypeName?.toLocaleLowerCase() === 'booklets' &&
    skuName?.toLocaleLowerCase().includes('cover')
  );
};

export const printingFrontViewQuoteForShippingCart = (
  pmsFront: string[],
  coatingFront: string,
  inkFront: string,
  skuName: string,
  skuTypeName: string
) => {
  const pmsFrontString = pmsFront?.filter((item) => item).join(',');
  const coatingFrontString = coatingFront !== 'none' ? coatingFront : '';
  const inkFrontString = inkFront !== 'none' ? inkFront : 'No Printing';
  const outsideString = [
    inkFrontString?.toLocaleLowerCase() === InkEnum.pms
      ? ''
      : getFriendlyDisplay(
          inkFrontString === 'black' ? 'Black & White' : inkFrontString
        ),
    pmsFrontString ? `PMS ${pmsFrontString}` : '',
    getFriendlyDisplay(coatingFrontString?.split('_')[1] || coatingFrontString),
  ]
    .filter((item) => item)
    .join(' + ');
  const skuTitle = isSkuBooklets(skuName, skuTypeName) ? 'Outside' : 'Front';
  return `${skuTitle}: ${outsideString}`;
};

export const printingBackViewQuoteForShippingCart = (
  pmsFront: string[],
  coatingFront: string,
  inkFront: string,
  skuName: string,
  skuTypeName: string
) => {
  const pmsFrontString = pmsFront?.filter((item) => item).join(',');
  const coatingFrontString = coatingFront !== 'none' ? coatingFront : '';
  const inkFrontString = inkFront !== 'none' ? inkFront : 'No Printing';
  const outsideString = [
    inkFrontString?.toLocaleLowerCase() === InkEnum.pms
      ? ''
      : getFriendlyDisplay(
          inkFrontString === 'black' ? 'Black & White' : inkFrontString
        ),
    pmsFrontString ? `PMS ${pmsFrontString}` : '',
    getFriendlyDisplay(coatingFrontString?.split('_')[1] || coatingFrontString),
  ]
    .filter((item) => item)
    .join(' + ');
  const skuTitle = isSkuBooklets(skuName, skuTypeName) ? 'Inside' : 'Back';
  return `${skuTitle}: ${outsideString}`;
};

export const getJobSpecPrintingFrontStringForStep = (
  partSpec: JobSpec,
  sku?: SKU
) => {
  const pmsFrontString = partSpec.pmsFront.length
    ? `${partSpec.pmsFront.length} PMS colors`
    : '';
  const coatingFrontString =
    partSpec.coatingFront !== 'none' ? partSpec.coatingFront : '';
  const inkFrontString =
    partSpec.inkFront !== 'none' ? partSpec.inkFront : 'No Printing';
  const outsideString = [
    inkFrontString?.toLocaleLowerCase() === InkEnum.pms
      ? ''
      : getFriendlyDisplay(
          inkFrontString === 'black' ? 'Black & White' : inkFrontString
        ),
    pmsFrontString,
    getFriendlyDisplay(coatingFrontString?.split('_')[1] || coatingFrontString),
  ]
    .filter((item) => item)
    .join(', ');
  const skuTitle = isSkuBooklets(sku?.name, sku?.skuType?.name)
    ? 'Outside'
    : 'Front';
  return `${skuTitle}: ${outsideString}`;
};

export const getJobSpecPrintingFrontStringForViewQuote = (
  partSpec: JobSpec,
  sku?: SKU
) => {
  const pmsFrontString = partSpec.pmsFront.filter((item) => item).join(',');
  const coatingFrontString =
    partSpec.coatingFront !== 'none' ? partSpec.coatingFront : '';
  const inkFrontString =
    partSpec.inkFront !== 'none' ? partSpec.inkFront : 'No Printing';
  const outsideString = [
    inkFrontString?.toLocaleLowerCase() === InkEnum.pms
      ? ''
      : getFriendlyDisplay(
          inkFrontString === 'black' ? 'Black & White' : inkFrontString
        ),
    pmsFrontString ? `PMS ${pmsFrontString}` : '',
    getFriendlyDisplay(coatingFrontString?.split('_')[1] || coatingFrontString),
  ]
    .filter((item) => item)
    .join(' + ');
  const skuTitle = isSkuBooklets(sku?.name, sku?.skuType?.name)
    ? 'Outside'
    : 'Front';
  return `${skuTitle}: ${outsideString}`;
};

export const getJobSpecPrintingBackStringForStep = (
  partSpec: JobSpec,
  sku?: SKU
) => {
  const pmsBackString = partSpec.pmsBack.length
    ? `${partSpec.pmsBack.length} PMS colors`
    : '';
  const coatingBackString =
    partSpec.coatingBack !== 'none' ? partSpec.coatingBack : '';
  const inkBackString =
    partSpec.inkBack !== 'none' ? partSpec.inkBack : 'No Printing';
  const insideString = [
    inkBackString?.toLocaleLowerCase() === InkEnum.pms
      ? ''
      : getFriendlyDisplay(
          inkBackString === 'black' ? 'Black & White' : inkBackString
        ),
    pmsBackString,
    getFriendlyDisplay(coatingBackString?.split('_')[1] || coatingBackString),
  ]
    .filter((item) => item)
    .join(', ');
  const skuTitle = isSkuBooklets(sku?.name, sku?.skuType?.name)
    ? 'Inside'
    : 'Back';
  return `${skuTitle}: ${insideString}`;
};

export const getJobSpecPrintingBackStringForViewQuote = (
  partSpec: JobSpec,
  sku?: SKU
) => {
  const pmsBackString = partSpec.pmsBack.filter((item) => item).join(',');
  const coatingBackString =
    partSpec.coatingBack !== 'none' ? partSpec.coatingBack : '';
  const inkBackString =
    partSpec.inkBack !== 'none' ? partSpec.inkBack : 'No Printing';
  const insideString = [
    inkBackString?.toLocaleLowerCase() === InkEnum.pms
      ? ''
      : getFriendlyDisplay(
          inkBackString === 'black' ? 'Black & White' : inkBackString
        ),
    pmsBackString ? `PMS ${pmsBackString}` : '',
    getFriendlyDisplay(coatingBackString?.split('_')[1] || coatingBackString),
  ]
    .filter((item) => item)
    .join(' + ');
  const skuTitle = isSkuBooklets(sku?.name, sku?.skuType?.name)
    ? 'Inside'
    : 'Back';
  return `${skuTitle}: ${insideString}`;
};

export const getJobSpecOptionsString = (partSpec: JobSpec) => {
  const svcs = partSpec?.optionalSvcDefs?.map((svc) => svc.name);
  if (svcs?.length) {
    return svcs.join(', ');
  } else {
    return 'None Selected';
  }
};

export const getJobSpecServiceString = (partSpec: JobSpec) => {
  const svcs = partSpec?.additionalJobOptionDefs?.map((svc) => svc.name);
  if (svcs?.length) {
    return svcs.join(', ');
  } else {
    return 'None Selected';
  }
};

export { StepTypeEnum } from './stepTypeEnum';

export type StepInfo = {
  type: StepTypeEnum;
  heading: string | number;
  title: string | number;
  subTitle?: string | number;
  partSpec?: JobSpec;
  specSvc?: JobOptionDef[];
  skuSvc?: Svc | SvcType;
  partNumber?: number;
  isPrefab?: boolean;
  isPackaging?: boolean;
};

export const getSteps = (
  configItem: JobConfig,
  sku: SKU,
  unitPriceConfigured: boolean,
  /** Measurement system the step summaries are rendered in. Storage stays imperial. */
  measurementUnit: MeasurementUnitEnum,
  galleryLabel?: SkuGalleryLabelCount | null,
  user?: User | null,
  isExpert?: boolean
) => {
  const steps: StepInfo[] = [];
  const selectedSpec = getSelectedSpecItem(configItem);
  const isPackaging = sku?.skuType?.name === SKU_TYPE_NAME_PACKAGING;

  // 3: optionalSvcGroup
  // 2: requiredSvcGroup
  // 1: additionalJobOptions
  // 0: jobOptions

  // booklet type step
  steps.push({
    type: StepTypeEnum.BOOKLET_TYLE,
    heading: `${(
      sku?.displayName ||
      sku?.galleryLabel?.name ||
      galleryLabel?.skuLabel?.name ||
      ''
    ).replace(/s$/, '')} Type`,
    title: `${sku.name || selectedSpec.spec.skuName}`,
  });

  // const isPrefab = sku.revxId === PRECONFIG_SKUID;

  // quantities
  steps.push({
    type: StepTypeEnum.QUANTITIES,
    heading: 'QUANTITIES',
    title: getJobConfigQtyString(configItem),
    isPrefab: false,
  });

  // page count
  if (!!selectedSpec.spec.parts.find((item) => item.pageCountEditable)) {
    steps.push({
      type: StepTypeEnum.PAGE_COUNT,
      heading: 'PAGE COUNT',
      title: getJobConfigPageCountString(configItem),
      isPrefab: sku.skipPageCount,
    });
  }

  // size orientation
  steps.push({
    type: StepTypeEnum.SIZE_ORIENTATION,
    heading: isPackaging ? 'SIZE' : 'SIZE - ORIENTATION',
    isPackaging: isPackaging,
    title: isPackaging
      ? getPackagingJobConfigSizeOrientationString(configItem, measurementUnit)
      : getJobConfigSizeOrientationString(configItem, measurementUnit),
    isPrefab: sku.skipSizeAndOrientation,
  });
  // job options
  if (
    selectedSpec.spec.jobOptions &&
    Boolean((selectedSpec.spec.showSvcFlg & (1 << 0)) !== 0)
  ) {
    steps.push({
      type: StepTypeEnum.JOB_OPTIONS,
      heading: 'JOB OPTIONS',
      title: [
        selectedSpec.spec.jobOptionDefs?.[0]?.name,
        selectedSpec.spec.jobOptionDefs?.[0]?.specDefs?.[0]?.parameters?.[0]
          ?.parameter,
      ]
        .filter((item) => item)
        .join(' - '),
      isPrefab: sku.skipJobOption,
      // title: `Staples ${
      //   selectedSpec.spec.bindingEdge === BINDING_EDGE_ENUM['Short Edge']
      //     ? 'length'
      //     : 'width'
      // } side`,
    });
  }

  // parts options
  selectedSpec.spec.parts.forEach((partSpec, idx) => {
    const partSku = sku?.parts?.length
      ? sku?.parts.find((item) => item.part.id === partSpec?.skuId)?.part
      : sku;
    const skipRequiredSvc =
      ((partSpec.showSvcFlg & (1 << 2)) !== 0 && partSku?.skipRequiredSvc) ||
      (partSpec.showSvcFlg & (1 << 2)) === 0;
    const skipOptionalSvc =
      ((partSpec.showSvcFlg & (1 << 3)) !== 0 && partSku?.skipOptionalSvc) ||
      (partSpec.showSvcFlg & (1 << 3)) === 0;
    steps.push({
      type: StepTypeEnum.PART_ACTION,
      heading: '',
      title: partSpec.skuDisplayName || partSpec.skuName || '',
      partSpec: partSpec,
      isPrefab:
        partSku?.skipInkAndFinish &&
        partSku?.skipPaperOptions &&
        skipRequiredSvc &&
        skipOptionalSvc,
    });
    steps.push({
      type: StepTypeEnum.PART_PRINTING,
      heading: `PRINTING - ${
        partSpec.skuDisplayName ||
        partSpec.skuName ||
        galleryLabel?.skuLabel?.name
      }`,
      partNumber: idx + 1,
      title: getJobSpecPrintingFrontStringForStep(partSpec, partSku),
      subTitle: getJobSpecPrintingBackStringForStep(partSpec, partSku),
      partSpec: partSpec,
      isPrefab: partSku?.skipInkAndFinish,
    });

    if (!partSpec.paperEquivalentCode) {
      steps.push({
        type: StepTypeEnum.PART_PAPER,
        heading: `PAPER - ${
          partSpec.skuDisplayName ||
          partSpec.skuName ||
          galleryLabel?.skuLabel?.name
        }`,
        partNumber: idx + 1,
        title: getJobSpecPaperString(partSpec, measurementUnit),
        partSpec: partSpec,
        isPrefab:
          partSku?.skipPaperOptions ||
          (partSku?.hidePaperConstraintSection &&
            partSku?.hidePaperSearchSection),
      });
    }

    if (
      (partSpec.optionalSvcGroup || partSpec.requiredSvcGroup) &&
      Boolean(
        (partSpec.showSvcFlg & (1 << 2)) !== 0 ||
          (partSpec.showSvcFlg & (1 << 3)) !== 0
      )
    ) {
      steps.push({
        type: StepTypeEnum.PART_OPTIONS,
        heading: `OPTIONS - ${
          partSpec.skuDisplayName ||
          partSpec.skuName ||
          galleryLabel?.skuLabel?.name
        }`,
        partNumber: idx + 1,
        title: getJobSpecOptionsString(partSpec),
        partSpec: partSpec,
        isPrefab: skipOptionalSvc && skipRequiredSvc,
      });
    }
  });

  // services
  if (
    (selectedSpec.spec?.additionalJobOptions &&
      Boolean((selectedSpec.spec?.showSvcFlg & (1 << 1)) !== 0)) ||
    isExpert
  ) {
    steps.push({
      type: StepTypeEnum.SERVICES,
      heading: 'SERVICES',
      title: getJobSpecServiceString(selectedSpec.spec),
      specSvc: selectedSpec.spec.additionalJobOptionDefs,
      partNumber: selectedSpec.spec.parts.length,
      isPrefab: sku?.skipAdditionalJobOption,
    });
  }

  // files
  steps.push({
    type: StepTypeEnum.ADD_FILES,
    heading: 'Art Files',
    title: 'Art Files',
    partNumber: selectedSpec.spec.parts.length,
    isPrefab: configItem?.skipArtFile,
  });

  // arrival date
  const deliveryTime =
    selectedSpec.spec.shippings?.[0].fulfillment.shippingInfo
      .estimatedDeliveryTime;
  steps.push({
    type: StepTypeEnum.ARRIVAL_DATE,
    heading: 'ARRIVAL DATE',
    title: deliveryTime
      ? getFormatDate({
          dateStr: deliveryTime,
        })
      : 'Arrival Date',
    partNumber: selectedSpec.spec.parts.length,
    isPrefab: false,
  });

  // view quote
  steps.push({
    type: StepTypeEnum.VIEW_QUOTE,
    heading: 'Review Quote',
    title: 'Quote',
    partNumber: selectedSpec.spec.parts.length,
  });

  if (
    user &&
    (isSourcer(user) || isSupplier(user)) &&
    !unitPriceConfigured &&
    !sku.isSpecifySupplier
  ) {
    steps.push({
      type: StepTypeEnum.COMPARISON,
      heading: 'Comparison',
      title: 'Comparison',
      partNumber: selectedSpec.spec.parts.length,
      isPrefab: false,
    });
  }

  return steps;
};

export const isTypeBoolean = (step: StepInfo) => {
  const typeBoolean = Boolean(
    [
      StepTypeEnum.PART_PRINTING,
      StepTypeEnum.PART_PAPER,
      StepTypeEnum.PART_OPTIONS,
    ].find((item) => item === step?.type)
  );
  return typeBoolean;
};

export const getGallerySteps = (
  galleryLabel: SkuGalleryLabelCount,
  sku?: GallerySku | null,
  title?: SkuGalleryLabel | null
) => {
  const steps: StepInfo[] = [];
  const isPackagingSku = sku?.typeName === SKU_TYPE_NAME_PACKAGING;

  // booklet type step
  steps.push({
    type: StepTypeEnum.BOOKLET_TYLE,
    heading: `${(galleryLabel.skuLabel.name || sku?.displayName || '').replace(
      /s$/,
      ''
    )} Type`,
    title:
      sku?.name ||
      title?.name ||
      (i18n.t('jobConfig.navStep.selectType.title') as string),
  });

  steps.push({
    type: StepTypeEnum.QUANTITIES,
    heading: i18n.t('jobConfig.navStep.quantity.heading') as string,
    title: i18n.t('jobConfig.navStep.quantity.title') as string,
  });

  steps.push({
    type: StepTypeEnum.PAGE_COUNT,
    heading: i18n.t('jobConfig.navStep.pageCount.heading') as string,
    title: i18n.t('jobConfig.navStep.pageCount.title') as string,
  });

  steps.push({
    type: StepTypeEnum.SIZE_ORIENTATION,
    heading: isPackagingSku
      ? (i18n.t('jobConfig.navStep.sizeOrientation.packageHeading') as string)
      : (i18n.t('jobConfig.navStep.sizeOrientation.heading') as string),
    title: isPackagingSku
      ? (i18n.t('jobConfig.navStep.sizeOrientation.packageTitle') as string)
      : (i18n.t('jobConfig.navStep.sizeOrientation.title') as string),
  });

  steps.push({
    type: StepTypeEnum.JOB_OPTIONS,
    heading: i18n.t('jobConfig.navStep.jobOptions.heading') as string,
    title: i18n.t('jobConfig.navStep.jobOptions.title') as string,
  });

  steps.push({
    type: StepTypeEnum.PART_PAPER,
    heading: i18n.t('jobConfig.navStep.paper.heading') as string,
    title: i18n.t('jobConfig.navStep.paper.title') as string,
  });

  steps.push({
    type: StepTypeEnum.PART_PRINTING,
    heading: i18n.t('jobConfig.navStep.printing.heading') as string,
    title: i18n.t('jobConfig.navStep.printing.title') as string,
  });

  steps.push({
    type: StepTypeEnum.PART_OPTIONS,
    heading: i18n.t('jobConfig.navStep.options.heading') as string,
    title: i18n.t('jobConfig.navStep.options.title') as string,
  });

  steps.push({
    type: StepTypeEnum.SERVICES,
    heading: i18n.t('jobConfig.navStep.services.heading') as string,
    title: i18n.t('jobConfig.navStep.services.title') as string,
  });

  steps.push({
    type: StepTypeEnum.ADD_FILES,
    heading: i18n.t('jobConfig.navStep.addFiles.heading') as string,
    title: i18n.t('jobConfig.navStep.addFiles.title') as string,
  });

  steps.push({
    type: StepTypeEnum.ARRIVAL_DATE,
    heading: i18n.t('jobConfig.navStep.arrivalDate.heading') as string,
    title: i18n.t('jobConfig.navStep.arrivalDate.title') as string,
  });

  return steps;
};
export const orientationType = (w: number, l: number) => {
  if (l >= w) {
    return OrientationEnum.portrait;
  } else {
    return OrientationEnum.landscape;
  }
};

export const formatNumber = (formatValue: string | number) => {
  const value = formatValue + '';
  const list = value.split('.');
  const prefix = list[0].charAt(0) === '-' ? '-' : '';
  let num = prefix ? list[0].slice(1) : list[0];
  let result = '';
  while (num.length > 3) {
    result = `,${num.slice(-3)}${result}`;
    num = num.slice(0, num.length - 3);
  }
  if (num) {
    result = num + result;
  }
  return `${prefix}${result}${list[1] ? `.${list[1]}` : ''}`;
};

export const roundNumber = (n: number) => {
  return _.round(n, 3);
};

export const plainOptionHandle: {
  [x: string]: (
    option: Partial<SkuSizePayload>,
    foldPanelCount: number
  ) => Partial<SkuSizePayload> | undefined;
} = {
  [CustomRuleCodeEnum.SFEP]: (
    option: Partial<SkuSizePayload>,
    foldPanelCount: number
  ) => {
    if (option.finishedWidth && option.finishedLength) {
      return {
        finishedWidth: option.finishedWidth,
        finishedLength: option.finishedLength,
        flatWidth: roundNumber(
          foldPanelCount <= 0 ? 0 : +option.finishedWidth * foldPanelCount
        ),
        flatLength: option.finishedLength,
        orientation: orientationType(
          option.finishedWidth,
          option.finishedLength
        ),
      };
    } else if (option.flatWidth && option.flatLength) {
      return {
        finishedWidth: roundNumber(
          foldPanelCount <= 0 ? 0 : +option.flatWidth / foldPanelCount
        ),
        finishedLength: option.flatLength,
        flatWidth: option.flatWidth,
        flatLength: option.flatLength,
        orientation: orientationType(option.flatWidth, option.flatLength),
      };
    }
  },
  [CustomRuleCodeEnum.CFUP]: (
    option: Partial<SkuSizePayload>,
    foldPanelCount: number
  ) => {
    if (option.finishedWidth && option.finishedLength) {
      return {
        finishedWidth: option.finishedWidth,
        finishedLength: option.finishedLength,
        flatWidth: roundNumber(
          foldPanelCount <= 0 ? 0 : +option.finishedWidth * (foldPanelCount - 1)
        ),
        flatLength: option.finishedLength,
        orientation: orientationType(
          option.finishedWidth,
          option.finishedLength
        ),
      };
    } else if (option.flatWidth && option.flatLength) {
      return {
        finishedWidth: roundNumber(
          foldPanelCount <= 0 ? 0 : +option.flatWidth / (foldPanelCount - 1)
        ),
        finishedLength: option.flatLength,
        flatWidth: option.flatWidth,
        flatLength: option.flatLength,
        orientation: orientationType(option.flatWidth, option.flatLength),
      };
    }
  },
  [CustomRuleCodeEnum.CFEBDP]: (
    option: Partial<SkuSizePayload>,
    foldPanelCount: number
  ) => {
    if (option.finishedWidth && option.finishedLength) {
      return {
        finishedWidth: option.finishedWidth,
        finishedLength: option.finishedLength,
        flatWidth: roundNumber(
          foldPanelCount <= 0 ? 0 : +option.finishedWidth * (foldPanelCount / 2)
        ),
        flatLength: roundNumber(
          foldPanelCount <= 0
            ? 0
            : +option.finishedLength * (foldPanelCount / 2)
        ),
        orientation: orientationType(
          option.finishedWidth,
          option.finishedLength
        ),
      };
    } else if (option.flatWidth && option.flatLength) {
      return {
        finishedWidth: roundNumber(
          foldPanelCount <= 0 ? 0 : +option.flatWidth / (foldPanelCount / 2)
        ),
        finishedLength: roundNumber(
          foldPanelCount <= 0 ? 0 : +option.flatLength / (foldPanelCount / 2)
        ),
        flatWidth: option.flatWidth,
        flatLength: option.flatLength,
        orientation: orientationType(option.flatWidth, option.flatLength),
      };
    }
  },
  [CustomRuleCodeEnum.CFUBDP]: (
    option: Partial<SkuSizePayload>,
    foldPanelCount: number
  ) => {
    if (option.finishedWidth && option.finishedLength) {
      return {
        finishedWidth: option.finishedWidth,
        finishedLength: option.finishedLength,
        flatWidth: roundNumber(
          foldPanelCount <= 0 ? 0 : +option.finishedWidth * (foldPanelCount / 2)
        ),
        flatLength: roundNumber(
          foldPanelCount <= 0
            ? 0
            : +option.finishedLength * (foldPanelCount / 3)
        ),
        orientation: orientationType(
          option.finishedWidth,
          option.finishedLength
        ),
      };
    } else if (option.flatWidth && option.flatLength) {
      return {
        finishedWidth: roundNumber(
          foldPanelCount <= 0 ? 0 : +option.flatWidth / (foldPanelCount / 2)
        ),
        finishedLength: roundNumber(
          foldPanelCount <= 0 ? 0 : +option.flatLength / (foldPanelCount / 3)
        ),
        flatWidth: option.flatWidth,
        flatLength: option.flatLength,
        orientation: orientationType(option.flatWidth, option.flatLength),
      };
    }
  },
  [CustomRuleCodeEnum.NONE]: (
    option: Partial<SkuSizePayload>,
    foldPanelCount: number
  ) => {
    if (option.finishedWidth && option.finishedLength) {
      return {
        finishedWidth: option.finishedWidth,
        finishedLength: option.finishedLength,
        flatWidth: option.finishedWidth,
        flatLength: option.finishedLength,
        orientation: orientationType(
          option.finishedWidth,
          option.finishedLength
        ),
      };
    } else if (option.flatWidth && option.flatLength) {
      return {
        finishedWidth: option.flatWidth,
        finishedLength: option.flatLength,
        flatWidth: option.flatWidth,
        flatLength: option.flatLength,
        orientation: orientationType(option.flatWidth, option.flatLength),
      };
    }
  },
  // [CustomRuleCodeEnum.R4PG]: (
  //   option: Partial<SkuSizePayload>,
  //   foldPanelCount: number
  // ) => {
  //   if (option.finishedWidth && option.finishedLength) {
  //     return {
  //       finishedWidth: option.finishedWidth,
  //       finishedLength: option.finishedLength,
  //       flatWidth: option.finishedWidth,
  //       flatLength: option.finishedLength,
  //       orientation: orientationType(
  //         option.finishedWidth,
  //         option.finishedLength
  //       ),
  //     };
  //   } else if (option.flatWidth && option.flatLength) {
  //     return {
  //       finishedWidth:
  //         foldPanelCount <= 0 ? 0 : +option.flatWidth / foldPanelCount,
  //       finishedLength: option.flatLength,
  //       flatWidth: option.flatWidth,
  //       flatLength: option.flatLength,
  //       orientation: orientationType(option.flatWidth, option.flatLength),
  //     };
  //   }
  // },
};

export const jobPartSvcHideBorder = (svcs?: JobConfigSvcOptionType) => {
  const requiredOptionSvc =
    svcs?.requiredSvcGroup.filter((svcGroup) => {
      if (svcGroup.exclusiveType === ExclusiveTypeEnum.ONE_OF) {
        return true;
      }
      const showSvcGroup = svcGroup.svcGroups.filter((cSvcGroup) => {
        if (cSvcGroup.exclusiveType === ExclusiveTypeEnum.ONE_OF) {
          return true;
        }

        const showSvc = cSvcGroup.svcs.filter((svc) => {
          if (
            svc.message ||
            svc.options ||
            svc.specs.length > 1 ||
            svc.showInstructions
          ) {
            return true;
          } else {
            return false;
          }
        });

        if (showSvc.length > 1) {
          return true;
        }

        return false;
      });
      const showSvc = svcGroup.svcs.filter((svc) => {
        if (
          svc.message ||
          svc.options ||
          svc.specs.length > 1 ||
          svc.showInstructions
        ) {
          return true;
        } else {
          return false;
        }
      });
      if (showSvc.length > 1) {
        return true;
      }
      if (showSvcGroup.length > 1) {
        return true;
      }
      return false;
    }) || [];
  return Boolean(
    !svcs?.optionalSvcGroup.length && requiredOptionSvc?.length < 2
  )
    ? 'jobconfig-jobOption-hide-border'
    : '';
};

export const purchaseOrderPrice = (orderItems: Partial<PurchaseOrder>) => {
  if (!orderItems.jobs?.length && orderItems.id) {
    return {
      orderTotal: orderItems.totalCharge,
      freight: orderItems.freightCharge,
      tax: orderItems.taxCharge,
      printing: orderItems.printingCharge,
    };
  }
  const orderTotal: number = +sum(
    orderItems.jobs?.map((item) => {
      return (
        (item.printingCharge || 0) +
        (item.hideFreightCost ? 0 : item.freightCharge || 0) +
        (item.taxCharge || 0)
      );
    })
  ).toFixed(2);
  const freight: number = +sum(
    orderItems.jobs?.map((item) =>
      item.hideFreightCost ? 0 : item.freightCharge || 0
    )
  ).toFixed(2);
  const tax: number = +sum(
    orderItems.jobs?.map((item) => item.taxCharge || 0)
  ).toFixed(2);
  const printing: number = +sum(
    orderItems.jobs?.map((item) => item.printingCharge || 0)
  ).toFixed(2);
  return {
    orderTotal,
    freight,
    tax,
    printing,
  };
};

export const updatePriceMessage = ({
  newOrderItem,
  oldOrderItem,
  serialNumber,
}: {
  newOrderItem: CartLineItem;
  oldOrderItem: JobConfig;
  serialNumber: number;
}) => {
  const newSpec = oldOrderItem.specItems.find(
    (specItem) => specItem.serialNumber === serialNumber
  )?.spec;

  const messageList: string[] = [];
  if (moment(oldOrderItem?.orderDate).isAfter(moment())) {
    if (
      newOrderItem.costLineItems.printingCharge !==
        newSpec?.fulfillment.printingCharge ||
      (newOrderItem.costLineItems.printingCharge !==
        newSpec?.fulfillment.printingCharge &&
        newOrderItem.costLineItems.freightCharge !==
          newSpec?.fulfillment.freightCharge)
    ) {
      messageList.push(
        `There is some change in price. Please confirm before ordering ${newSpec?.skuDisplayName}`
      );
    }
    if (
      newOrderItem.costLineItems.freightCharge !==
        newSpec?.fulfillment.freightCharge &&
      newOrderItem.costLineItems.printingCharge ===
        newSpec?.fulfillment.printingCharge
    ) {
      messageList.push(
        `There is some change in shipping. Please confirm before ordering ${newSpec?.skuDisplayName}`
      );
    }
  } else {
    if (
      newOrderItem.costLineItems.printingCharge !==
        newSpec?.fulfillment.printingCharge ||
      (newOrderItem.costLineItems.printingCharge !==
        newSpec?.fulfillment.printingCharge &&
        newOrderItem.costLineItems.freightCharge !==
          newSpec?.fulfillment.freightCharge)
    ) {
      // messageList.push(
      //   `There is some change in price and shipping if you order it today. Please confirm before ordering ${newSpec?.skuDisplayName}`
      // );
      messageList.push(
        `The shipping price has changed. Please review the pricing before you order.`
      );
    }
    if (
      newOrderItem.costLineItems.freightCharge !==
        newSpec?.fulfillment.freightCharge &&
      newOrderItem.costLineItems.printingCharge ===
        newSpec?.fulfillment.printingCharge
    ) {
      messageList.push(
        `There is some change in shipping if you order it today. Please confirm before ordering ${newSpec?.skuDisplayName}`
      );
    }
  }

  // messageList.forEach((messageStr) => {
  //   toast(messageStr);
  // });
  if (messageList.length > 0) {
    toast(
      messageList.map((message, idx) =>
        React.createElement('div', null, message)
      )
    );
  }
};
